// File: src/pages/LoginPage.jsx
// Description: Modern, professional login page with enhanced visual appeal and user experience.

import React, { useState, useContext } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Link as MuiLink, 
  Card, 
  CardContent,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  useTheme,
  Snackbar
} from '@mui/material';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { BASE_URL } from '../config';
import { createAuthenticatedAPI } from '../utils/apiClient';

// Icons
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import GoogleIcon from '@mui/icons-material/Google';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';

const LoginPage = () => {
  const { login, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError(null); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // 1) LOGIN: POST /auth/login
      const loginResponse = await fetch(`${BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!loginResponse.ok) {
        const errorData = await loginResponse.json();
        throw new Error(errorData.detail || 'Login failed.');
      }

      const tokenData = await loginResponse.json();
      const token = tokenData.access_token;
      if (!token) {
        throw new Error('No access token received from login endpoint');
      }

      // 2) GET USER DATA: /users/me
      const userResponse = await fetch(`${BASE_URL}/users/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!userResponse.ok) {
        const userError = await userResponse.json();
        throw new Error(userError.detail || 'Could not fetch user data.');
      }

      const userData = await userResponse.json();

      // 3) STORE IN CONTEXT (spread all returned fields)
      login({
        token,
        ...userData,
      });

      // 4) Check for pending saved profile in localStorage
      const pendingProfileData = localStorage.getItem('pendingSavedProfile');
      if (pendingProfileData) {
        const pendingProfile = JSON.parse(pendingProfileData);
        try {
          // Create API client with user data including token
          const userDataWithToken = { token, ...userData };
          const api = createAuthenticatedAPI({ user: userDataWithToken, logout, navigate });
          
          await api.post('/users/save-profile', pendingProfile);
          
          // Clear only on success and notify
          localStorage.removeItem('pendingSavedProfile');
          setShowSuccessSnackbar(true);
        } catch (err) {
          console.error('Error saving pending profile:', err);
          // Keep pending so a future login can retry
        }
      }

      // 5) Navigate user to the dashboard
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    // TODO: Implement social login
    console.log(`Logging in with ${provider}`);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      display: 'flex',
      alignItems: 'center',
      py: 4
    }}>
      <Container maxWidth="sm">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography 
            variant="h3" 
            gutterBottom 
            sx={{ 
              fontWeight: 800,
              background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2
            }}
          >
            Welcome Back
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Sign in to your Touch Connect account
          </Typography>
        </Box>

        {/* Login Form Card */}
        <Card sx={{
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          border: `1px solid ${theme.palette.divider}`
        }}>
          <CardContent sx={{ p: 4 }}>
            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {/* Login Form */}
            <Box component="form" onSubmit={handleSubmit} sx={{ mb: 4 }}>
              {/* Email Field */}
              <TextField
                fullWidth
                name="email"
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
              />

              {/* Password Field */}
              <TextField
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                required
                sx={{ mb: 4 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
              />

              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                variant="gradient"
                size="large"
                disabled={isLoading}
                sx={{
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  mb: 3
                }}
              >
                {isLoading ? 'Signing In...' : 'Sign In'}
              </Button>

              {/* Forgot Password Link */}
              <Box sx={{ textAlign: 'center' }}>
                <Link
                  component={Link}
                  to="/forgot-password"
                  variant="body2"
                  sx={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Forgot your password?
                </Link>
              </Box>
            </Box>

            {/* Divider */}
            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">
                or continue with
              </Typography>
            </Divider>

            {/* Social Login Buttons */}
            <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<GoogleIcon />}
                onClick={() => handleSocialLogin('Google')}
                sx={{
                  borderRadius: 2,
                  borderWidth: '2px',
                  py: 1.5,
                  '&:hover': {
                    borderColor: theme.palette.error.main,
                    color: theme.palette.error.main
                  }
                }}
              >
                Google
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<LinkedInIcon />}
                onClick={() => handleSocialLogin('LinkedIn')}
                sx={{
                  borderRadius: 2,
                  borderWidth: '2px',
                  py: 1.5,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main
                  }
                }}
              >
                LinkedIn
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<GitHubIcon />}
                onClick={() => handleSocialLogin('GitHub')}
                sx={{
                  borderRadius: 2,
                  borderWidth: '2px',
                  py: 1.5,
                  '&:hover': {
                    borderColor: theme.palette.text.primary,
                    color: theme.palette.text.primary
                  }
                }}
              >
                GitHub
              </Button>
            </Box>

            {/* Sign Up Link */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Don't have an account?
              </Typography>
              <MuiLink
                component={Link}
                to="/register"
                variant="body2"
                sx={{
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Sign up for free
              </MuiLink>
            </Box>
          </CardContent>
        </Card>

        {/* Trust Indicators */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="caption" color="text.secondary">
            🔒 Your data is protected with enterprise-grade security
          </Typography>
        </Box>

        {/* Success Snackbar */}
        <Snackbar
          open={showSuccessSnackbar}
          autoHideDuration={4000}
          onClose={() => setShowSuccessSnackbar(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setShowSuccessSnackbar(false)}
            severity="success"
            variant="filled"
            sx={{ 
              width: '100%',
              borderRadius: 2
            }}
          >
            Profile has been added to your network! 🎉
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default LoginPage;
